import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { Formik, Form, useFormik } from "formik";
import _, { round } from "lodash";
import { AgGridReact } from "ag-grid-react";
import { Icon } from "@iconify/react";
import * as Yup from "yup";
import { HStack, VStack, Error } from "../components/utils";
import { Input, Label, TextArea } from "../components/shared/InputField";
import { useAuthStore } from "../store";
import { AntiDilutionAllocationType, AntiDilutionSecurity, InvestorDetails } from "../types/previewQuickRound";
import { Select } from "../components/shared/Select";
import { ButtonSize, PrimaryCTAButton, SecondaryCTAButton } from "../pages/quickRound/CTAButtonComponents";
import { useSecurityOverviewDetails } from "../queries/securities";
import { ShareholderDetails } from "../types/SecurityModel";
import AGGridAntiDilutionShareholdersTable from "../pages/newQuickTransaction/AntidilutionShareholderAgGridCaptable";
import { InstrumentClass } from "../types/DashboardModel";
import { getCurrencySymbol } from "../utils/currencyFormatter";
import { useGetPpsAndValuation } from "../queries/transactionRound";
import Tooltip from "../components/shared/Tooltip";

type AddShareholderProfileProps = {
  initialValue?: AntiDilutionSecurity;
  onPrimaryAction: (data: AntiDilutionSecurity) => void;
  onSecondaryAction: () => void;
  instrumentClasses: InstrumentClass[];
  mode: "Update" | "View";
  updateSecurityConversionDetails?: Function;
};

const UpdateAntiDilutionSecurityDetails = ({
  initialValue,
  onPrimaryAction = () => {},
  onSecondaryAction = () => {},
  instrumentClasses,
  mode,
  updateSecurityConversionDetails
}: AddShareholderProfileProps) => {
  const { isPlaceholderData, data: securityDetail } =
    useSecurityOverviewDetails();

  const [allocationMethod, setAllocationMethod] = useState(
    !initialValue?.allocationMethod ||
      initialValue?.allocationMethod === AntiDilutionAllocationType.None ?
        AntiDilutionAllocationType.None :
    initialValue?.useConversionRatio ?
    AntiDilutionAllocationType.UpdateConversionRatio :
    AntiDilutionAllocationType.IssueShares
  );

  const [investors, setInvestors] =
    useState<InvestorDetails[]>([]);

  const [eventId, setEventId] = useState("");

  useEffect(() => {
    const shareholders = securityDetail?.tableDetails
    ?.find((security) => security.id === initialValue?.instrumentClassId)?.shareHolder || [];

    const roundId = securityDetail?.tableDetails
    ?.find((security) => security.id === initialValue?.instrumentClassId)?.roundIdentifierDetails?.[0]?.roundId || "";

    const investorDetails =
      initialValue &&
      (initialValue?.investorDetails?.length || 0) > 0 ? [
        ...initialValue?.investorDetails?.map((investor) => ({
          ...investor,
          shareholderName:
          shareholders.find((sh) => sh.shareholderId === initialValue.investorId)?.shareholderName,
        })) || []
       ] :
      shareholders.map((shareholder) => ({
        investorId: shareholder.shareholderId,
        investorName: shareholder.shareholderName,
        numberOfShares: 0,
        additionalShares: 0,
        finalFdbShares: 0,
    }));
    if (!_.isEqual(investors, investorDetails))
      setInvestors(investorDetails);

    if (roundId !== eventId)
      setEventId(roundId);
  }, [securityDetail]);

  const { data: ppsData } = useGetPpsAndValuation();

  const pps = useMemo(() =>
    ppsData?.find((event:any) => event.eventId === eventId)?.pps || 1,
  [ppsData, eventId]);

  const originalRatio = useMemo(() =>
    instrumentClasses
    ?.find((instrument) => instrument.id === initialValue?.instrumentClassId)?.conversionRatio || 1,
  [instrumentClasses, initialValue?.instrumentClassId]);

  const initialValues: AntiDilutionSecurity = {
    instrumentClassId: initialValue?.instrumentClassId || "",
    adjustedPrice: initialValue?.adjustedPrice || pps || undefined,
    useConversionRatio: initialValue?.useConversionRatio || false,
    allocationMethod: initialValue?.allocationMethod || "",
    investorDetails: initialValue?.investorDetails || [],
    ratio: initialValue?.ratio ?? originalRatio,
  };

  const antidilutionSecuritySchema = Yup.object({
      allocationMethod: Yup.string().required().label("Allocation Method"),
      investorId: Yup.string().label("Shareholder Profile ID"),
      instrumentClassId: Yup.string().label("Security ID"),
      adjustedPrice: Yup.number().label("Adjusted Price"),
      useConversionRatio: Yup.boolean().label("Use Conversion Ratio"),
      antiDilutionShares: Yup.number().label("Anti Dilution Shares"),
      ratio: Yup.number().label("Ratio"),
      investorDetails: Yup.array(),
      antiDilutionMethod: Yup.string().label("Anti Dilution Method"),
    });

  const formik = useFormik<AntiDilutionSecurity>({
    initialValues,
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: antidilutionSecuritySchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
    onReset: () => {},
  });

  const updateRatio = (ratio: number) => {
    formik.setFieldValue("ratio", ratio);
  };

  useEffect(() => {
    formik.setFieldValue("allocationMethod", allocationMethod);
    if (!allocationMethod ||
      allocationMethod === AntiDilutionAllocationType.None
    )
      formik.setFieldValue("useConversionRatio", false);
    else
      formik.setFieldValue("useConversionRatio",
        allocationMethod === AntiDilutionAllocationType.UpdateConversionRatio
      );
  }, [
    allocationMethod,
    formik.values.adjustedPrice,
    pps
  ]);

  useEffect(() => {
    updateSecurityConversionDetails?.({
      ...formik.values,
      investorDetails: investors,
    });
  }, [investors]);

  const handleSubmit = (values: AntiDilutionSecurity) => {
    if (mode === "View") return;
    values = {
      ...values,
      investorDetails: investors,
    };
    onPrimaryAction(values);
  };

  const securityName = instrumentClasses
    ?.find((instrument) => instrument.id === initialValue?.instrumentClassId)
    ?.name;

  const currencySymbol = getCurrencySymbol();
  const gridRefAntiDilutionShareholder = useRef<AgGridReact>(null);

  return (
    <div>
      <Box className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          {`Update Anti-Dilution Shares ${securityName ? `for ${securityName}` : ""}`}
          <span onClick={() => onSecondaryAction()} className="cursor-pointer">
            X
          </span>
        </h6>
      </Box>
      <VStack className="w-full px-10 py-7 gap-9">
        <>
          <form onSubmit={formik.handleSubmit}>
            <VStack className="w-full gap-4">
              <HStack className="gap-10">
                <div className="flex-1">
                  <HStack className="gap-1 items-center">
                    <Label className="text-sm font-normal">Allocation Method</Label>
                    <Tooltip text={`Update Conversion Ratio: Adjust the conversion ratio of existing securities based on the adjusted price.
                      Issue Shares: Allocate additional shares to existing shareholders to maintain their ownership percentage.`}
                      _className="w-72">
                      <Icon icon="mdi:information-outline" width={16} height={16} />
                    </Tooltip>
                  </HStack>
                  <Select
                    options={[AntiDilutionAllocationType.UpdateConversionRatio,
                      AntiDilutionAllocationType.IssueShares,
                      AntiDilutionAllocationType.None]}
                    placeholder="Select Allocation Method"
                    className={`lg:w-72 sm:w-44`}
                    onChange={(e) => {
                      setAllocationMethod(e.target.value as AntiDilutionAllocationType);
                    }}
                    value={allocationMethod}
                    disabled={mode === "View"}
                  />
                  {formik.touched.useConversionRatio && formik.errors.useConversionRatio && (
                    <Error text={formik.errors.useConversionRatio} />
                  )}
                </div>
                <div className="flex-1">
                  <HStack className="gap-1 items-center">
                    <Label className="text-sm font-normal">Adjusted Price ({currencySymbol})</Label>
                    <Tooltip text={`The adjusted price per share which impacts the conversion ratio or share allocation.`}
                        _className="w-72">
                        <Icon icon="mdi:information-outline" width={16} height={16} />
                      </Tooltip>
                  </HStack>
                  <Input
                    type="number"
                    placeholder="Enter Adjusted Price"
                    className={`w-72`}
                    {...formik.getFieldProps("adjustedPrice")}
                    disabled={mode === "View"}
                  />
                  {formik.touched.adjustedPrice && formik.errors.adjustedPrice && (
                    <Error text={formik.errors.adjustedPrice} />
                  )}
                </div>
                <div className="flex-1">
                </div>
              </HStack>
              <HStack className="gap-10 mt-5">
                <div className="flex-1">
                    <Label className="text-sm font-normal">Round PPS ({currencySymbol})</Label>
                    <div className="text-xl font-normal">{parseFloat(pps?.toFixed(2))}</div>
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">Original Conversion Ratio</Label>
                    <div className="text-xl font-normal">{`1:${originalRatio}`}</div>
                  </div>
                <div className="flex-1">
                {formik.values.ratio &&
                  formik.values.allocationMethod
                    === AntiDilutionAllocationType.UpdateConversionRatio &&
                  <div>
                    <Label className="text-sm font-normal">Updated Conversion Ratio</Label>
                    <div className="text-xl font-normal">{`1:${parseFloat(formik.values.ratio?.toFixed(4))}`}</div>
                  </div>}
                </div>
              </HStack>
              <HStack className="gap-10"></HStack>
              <Box>
                <AGGridAntiDilutionShareholdersTable
                  investors={investors}
                  setInvestors={setInvestors}
                  instrumentClassId={initialValue?.instrumentClassId || ""}
                  ratio={((pps || 0) / (formik.values.adjustedPrice || 1))}
                  originalRatio={originalRatio}
                  gridRefAntiDilutionShareholder={gridRefAntiDilutionShareholder}
                  allocationMethod={formik.values.allocationMethod || ""}
                  updateRatio={updateRatio}
                  mode={mode}/>
              </Box>
              <HStack className="justify-between h-8">
                <SecondaryCTAButton
                  event-name="Cancel Founder Modal"
                  buttonSize={ButtonSize.MEDIUM}
                  onClick={() => {
                    onSecondaryAction();
                  }}
                >
                  Cancel
                </SecondaryCTAButton>
                <PrimaryCTAButton
                  className={mode === "View" ? `hidden` : ""}
                  event-name="Save Founder Modal"
                  buttonSize={ButtonSize.SMALL}
                  onClick={() => {
                    if (mode === "View") return;

                    const rowData: any[] = [];
                    gridRefAntiDilutionShareholder
                      ?.current?.api.forEachNode((node) => rowData.push(node.data));
                    onPrimaryAction({
                      ...formik.values,
                      investorDetails: rowData,
                    });
                  }}
                >
                  Save
                </PrimaryCTAButton>
              </HStack>
            </VStack>
          </form>
        </>
      </VStack>
    </div>
  );
};

export default UpdateAntiDilutionSecurityDetails;
